import { SimpleGrid } from '@mantine/core';
import {
  useGetGuest,
  useGetGuestCustomerRelations,
  useGetGuestLastTransactions,
  useGetGuestSaleTotal,
  useGetGuestTopBookingPreferences,
  useGetGuestTopFrequency,
  useGetGuestUpcomingTrips,
} from 'api-hooks/guest';
import { Option } from 'baseui/select';
import { NavigationRoutes } from 'common/constants';
import EmptyView from 'components/common/empty-view';
import LoadingComponent from 'components/common/loading';
import Separator from 'components/common/separator';
import ErrorViewComponent from 'components/elements/error-view-component';
import FetchWrapperComponent from 'components/elements/fetch-wrapper-component';
import Select from 'components/elements/master/select-input';
import Text from 'components/elements/text';
import useGuestSelectAction from 'hooks/use-guest-select-action';
import {
  CardSection,
  DetailContainer,
  FullContainer,
  HalfContainer,
  SectionContainer,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { NavigationProps } from 'types';

import DescriptionForm from './components/description-form';
import GuestInformation from './components/guest-information';
import SaleChart from './components/sale-chart';
import BookingPreferenceTable from './components/tables/booking-preference-table';
import CustomerRelationTable from './components/tables/customer-relation-table';
import GuestRelationTable from './components/tables/guest-relation-table';
import LastTransactionTable from './components/tables/last-transaction-table';
import TopTable from './components/tables/top-table';
import UpcomingTable from './components/tables/upcoming-table';

export type DashboardGuestViewParams = {
  id: string;
};

export default function DashboardGuestView(
  props: NavigationProps<NavigationRoutes.GuestDashboardView>,
) {
  const { guestId, onChange, guests, value } = useGuestSelectAction(
    props.navigation.params?.id,
  );
  const { t } = useTranslation();

  const options = React.useMemo<Option[]>(
    () => [
      { id: undefined, label: t('common:all_time') },
      { id: 3, label: t('common:last_extra_months', { extra: 3 }) },
      { id: 6, label: t('common:last_extra_months', { extra: 6 }) },
      { id: 12, label: t('common:last_extra_months', { extra: 12 }) },
    ],
    [t],
  );

  const [monthLimit, setMonthLimit] = React.useState<
    { id: any; label: string }[]
  >([]);

  const { data: guestSaleData, isFetching: loadingChart } =
    useGetGuestSaleTotal(
      { id: guestId!, month_limit: monthLimit[0]?.id },
      { enabled: !!guestId },
    );

  const {
    data: guestInformationData,
    isFetching: fetchingInformation,
    refetch: refetchInformation,
    isError: errorLoading,
  } = useGetGuest({ id: guestId! }, { enabled: !!guestId });

  const { data: guestTopFrequencyData, isFetching: loadingTop } =
    useGetGuestTopFrequency({ id: guestId! }, { enabled: !!guestId });

  const { data: guestUpcomingTripsData, isFetching: loadingUpcoming } =
    useGetGuestUpcomingTrips({ id: guestId! }, { enabled: !!guestId });

  const { data: customerRelationData, isFetching: loadingCustomerRelation } =
    useGetGuestCustomerRelations({ id: guestId! }, { enabled: !!guestId });

  const { data: lastTransactionData, isFetching: loadingLastTransaction } =
    useGetGuestLastTransactions({ id: guestId! }, { enabled: !!guestId });

  const { data: topBookingData, isFetching: loadingTopBooking } =
    useGetGuestTopBookingPreferences({ id: guestId! }, { enabled: !!guestId });

  return (
    <>
      <div>
        <div
          style={{
            maxWidth: 350,
          }}
        >
          <Text variant="body2Regular">Guest</Text>
          <Separator gap={4} />
          <Select
            placeholder={t('common:select_guest')}
            options={guests}
            onChange={onChange}
            value={value}
          />
        </div>

        <Separator gap={16} />

        {!guestId && <EmptyView />}

        <FetchWrapperComponent
          onRetry={refetchInformation}
          loadingComponent={<LoadingComponent />}
          error={!!errorLoading}
          errorComponent={
            <DetailContainer>
              <ErrorViewComponent refetch={refetchInformation} />
            </DetailContainer>
          }
          isLoading={fetchingInformation}
          component={
            !!guestInformationData?.data && (
              <>
                <GuestInformation data={guestInformationData.data} />
                <Separator gap={24} />
                <DescriptionForm guest={guestInformationData.data} />
                <Separator gap={24} />
                <CardSection>
                  <SectionContainer>
                    <HalfContainer>
                      <Text variant="HeadingXSmall">
                        {t('common:bookings')}
                      </Text>
                    </HalfContainer>
                    <HalfContainer>
                      <Select
                        value={monthLimit}
                        options={options}
                        onChange={(params) => {
                          setMonthLimit(params.value as any);
                        }}
                      />
                    </HalfContainer>
                  </SectionContainer>
                  {loadingChart ? (
                    <div style={{ width: '100%', margin: 'auto', height: 300 }}>
                      <LoadingComponent />
                    </div>
                  ) : (
                    <SaleChart data={guestSaleData?.data || []} />
                  )}
                </CardSection>
                <Separator gap={24} />
                <FullContainer>
                  <UpcomingTable
                    data={guestUpcomingTripsData?.data || []}
                    isLoading={loadingUpcoming}
                  />
                </FullContainer>
                <Separator gap={24} />
                <FullContainer>
                  <LastTransactionTable
                    data={lastTransactionData?.data || []}
                    isLoading={loadingLastTransaction}
                  />
                </FullContainer>
                <Separator gap={24} />
                <FullContainer>
                  <BookingPreferenceTable
                    data={topBookingData?.data || []}
                    isLoading={loadingTopBooking}
                  />
                </FullContainer>
                <Separator gap={24} />

                <SimpleGrid
                  cols={{
                    base: 1,
                    sm: 1,
                    md: 2,
                    lg: 3,
                  }}
                  spacing="xl"
                >
                  <TopTable
                    type="airline"
                    data={guestTopFrequencyData?.data?.airlines || []}
                    isLoading={loadingTop}
                  />
                  <TopTable
                    type="hotel"
                    data={guestTopFrequencyData?.data?.hotels || []}
                    isLoading={loadingTop}
                  />
                  <TopTable
                    type="cruise"
                    data={guestTopFrequencyData?.data?.cruises || []}
                    isLoading={loadingTop}
                  />
                  <TopTable
                    type="travel_document"
                    data={guestTopFrequencyData?.data?.travelDocuments || []}
                    isLoading={loadingTop}
                  />
                  <TopTable
                    type="activity"
                    data={guestTopFrequencyData?.data?.activities || []}
                    isLoading={loadingTop}
                  />
                </SimpleGrid>

                <Separator gap={24} />
                <SimpleGrid cols={{ sm: 1, md: 2 }} spacing="xl">
                  <CustomerRelationTable
                    data={customerRelationData?.data || []}
                    isLoading={loadingCustomerRelation}
                  />
                  <GuestRelationTable
                    data={guestInformationData?.data?.relations || []}
                    isLoading={fetchingInformation}
                  />
                </SimpleGrid>
                <Separator gap={24} />
              </>
            )
          }
        />
      </div>
    </>
  );
}
